import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import FloatingProcessingLogPanel from './FloatingProcessingLogPanel';

const COLLAPSED_MSG_MS = 120;
const HOURS_PER_YEAR = 8760;

/**
 * Owns processing-log state so addLog / stagger ticks do not re-render GenerationProfileChart.
 * Exposes imperative API via ref for parent callbacks.
 */
const ProcessingLogHost = forwardRef(function ProcessingLogHost(
  {
    simulationReady,
    capacityError,
    capacityLoading,
    chartHasHourlyProfile,
    loading,
    capacityLoadingGuard,
    solarLoading,
    windLoading,
    windOffLoading,
    solarProfileLength,
    windProfileLength,
    nuclearProfileLength,
  },
  ref
) {
  const [statusLogs, setStatusLogs] = useState([]);
  const [isProcessing, setIsProcessingState] = useState(false);
  const [hasError, setHasErrorState] = useState(false);
  const [floatingLogExpanded, setFloatingLogExpanded] = useState(false);
  const [logPanelDismissed, setLogPanelDismissed] = useState(false);
  const [collapsedDisplayIndex, setCollapsedDisplayIndex] = useState(0);
  const statusLogsRef = useRef(statusLogs);
  const logPanelDismissedRef = useRef(false);

  useEffect(() => {
    statusLogsRef.current = statusLogs;
  }, [statusLogs]);

  useEffect(() => {
    logPanelDismissedRef.current = logPanelDismissed;
  }, [logPanelDismissed]);

  const restorePanelAfterDismiss = useCallback(() => {
    if (!logPanelDismissedRef.current) return;
    setLogPanelDismissed(false);
    setFloatingLogExpanded(true);
  }, []);

  const appendEntries = useCallback((entries) => {
    if (!entries.length) return;
    restorePanelAfterDismiss();
    setStatusLogs((prev) => {
      const next = [...prev, ...entries];
      statusLogsRef.current = next;
      return next;
    });
  }, [restorePanelAfterDismiss]);

  const addLog = useCallback((message, source = 'frontend') => {
    const timestamp = new Date().toLocaleTimeString();
    appendEntries([{ timestamp, message, source }]);
  }, [appendEntries]);

  const addLogs = useCallback((messages, source = 'frontend') => {
    if (!messages?.length) return;
    const timestamp = new Date().toLocaleTimeString();
    appendEntries(messages.map((message) => ({ timestamp, message, source })));
  }, [appendEntries]);

  const addErrorLog = useCallback((message) => {
    const timestamp = new Date().toLocaleTimeString();
    appendEntries([{ timestamp, message, source: 'error' }]);
    setHasErrorState(true);
  }, [appendEntries]);

  const addSeparator = useCallback(() => {
    appendEntries([{ timestamp: '', message: '---SEPARATOR---', source: 'frontend' }]);
  }, [appendEntries]);

  const clearLogs = useCallback(() => {
    setStatusLogs([]);
    setHasErrorState(false);
    setCollapsedDisplayIndex(0);
    setFloatingLogExpanded(false);
    setLogPanelDismissed(true);
  }, []);

  const setIsProcessing = useCallback((value) => {
    setIsProcessingState((prev) => {
      const next = typeof value === 'function' ? value(prev) : value;
      if (next && !prev) {
        restorePanelAfterDismiss();
      }
      return next;
    });
  }, [restorePanelAfterDismiss]);

  const setHasError = useCallback((value) => {
    setHasErrorState(value);
  }, []);

  const showLatest = useCallback(() => {
    const lastIdx = Math.max(0, statusLogsRef.current.length - 1);
    setCollapsedDisplayIndex(lastIdx);
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      addLog,
      addLogs,
      addErrorLog,
      addSeparator,
      clearLogs,
      setIsProcessing,
      setHasError,
      showLatest,
    }),
    [addLog, addLogs, addErrorLog, addSeparator, clearLogs, setIsProcessing, setHasError, showLatest]
  );

  useEffect(() => {
    if (statusLogs.length === 0) {
      setCollapsedDisplayIndex(0);
    }
  }, [statusLogs.length]);

  // When processing finishes, snap collapsed preview to the latest message immediately.
  useEffect(() => {
    if (isProcessing) return;
    if (statusLogs.length === 0) return;
    setCollapsedDisplayIndex(statusLogs.length - 1);
  }, [isProcessing, statusLogs.length]);

  // Stagger collapsed preview only while actively processing.
  useEffect(() => {
    if (!isProcessing || statusLogs.length === 0) return undefined;
    const id = setInterval(() => {
      setCollapsedDisplayIndex((i) => {
        const target = statusLogs.length - 1;
        if (i >= target) return i;
        return i + 1;
      });
    }, COLLAPSED_MSG_MS);
    return () => clearInterval(id);
  }, [isProcessing, statusLogs.length]);

  const collapsedDisplayMessage = useMemo(() => {
    if (statusLogs.length === 0) return null;
    const idx = Math.min(collapsedDisplayIndex, statusLogs.length - 1);
    const log = statusLogs[idx];
    if (log.message === '---SEPARATOR---') {
      const prev = statusLogs.slice(0, idx).filter((l) => l.message !== '---SEPARATOR---').pop();
      return prev ? prev.message : 'New request';
    }
    return log.message;
  }, [statusLogs, collapsedDisplayIndex]);

  const collapsedPreviewText = useMemo(() => {
    if (capacityError && !capacityLoading) {
      return `Capacity failed: ${capacityError}`;
    }
    return collapsedDisplayMessage ?? (isProcessing ? 'Processing...' : 'No activity yet');
  }, [collapsedDisplayMessage, isProcessing, capacityError, capacityLoading]);

  const isComplete = !isProcessing && simulationReady;

  const handleExpand = useCallback(() => setFloatingLogExpanded(true), []);
  const handleCollapse = useCallback(() => setFloatingLogExpanded(false), []);
  const handleClearLogs = useCallback(
    (e) => {
      e?.stopPropagation?.();
      clearLogs();
    },
    [clearLogs]
  );

  // Clear spinner if profiles cannot complete simulation (e.g. failed hourly fetch).
  useEffect(() => {
    if (!isProcessing || simulationReady) return;
    if (loading || capacityLoadingGuard || solarLoading || windLoading || windOffLoading) return;
    if (!chartHasHourlyProfile) return;

    const profileReady = (len) => len === HOURS_PER_YEAR;
    const canSimulate =
      profileReady(solarProfileLength) &&
      profileReady(windProfileLength) &&
      profileReady(nuclearProfileLength);

    if (!canSimulate) {
      setIsProcessing(false);
    }
  }, [
    isProcessing,
    simulationReady,
    loading,
    capacityLoadingGuard,
    solarLoading,
    windLoading,
    windOffLoading,
    chartHasHourlyProfile,
    solarProfileLength,
    windProfileLength,
    nuclearProfileLength,
    setIsProcessing,
  ]);

  if (logPanelDismissed) {
    return null;
  }

  return (
    <FloatingProcessingLogPanel
      expanded={floatingLogExpanded}
      onExpand={handleExpand}
      onCollapse={handleCollapse}
      onClear={handleClearLogs}
      logs={statusLogs}
      isProcessing={isProcessing}
      hasError={hasError}
      isComplete={isComplete}
      collapsedPreviewText={collapsedPreviewText}
    />
  );
});

export default ProcessingLogHost;
